import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";

export default function DistributorRegister() {
    const { refresh } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({
        email: "", password: "", contact_name: "", business_name: "", phone: "",
        gst_number: "", license_number: "", address_line1: "", address_line2: "",
        city: "", state: "", pincode: "",
    });
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        for (const r of ["email", "password", "contact_name", "business_name", "phone", "address_line1", "city", "state", "pincode"]) {
            if (!f[r]) return toast.error("Please fill all required fields");
        }
        setBusy(true);
        try {
            const { data } = await api.post("/distributors/register", f);
            localStorage.setItem("hp_token", data.token);
            await refresh();
            toast.success("Application submitted! Awaiting admin approval.");
            nav("/distributor");
        } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="distributor-register">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Wholesale partner</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2 mb-2">Distributor application</h1>
            <p className="text-sm text-stone-600 mb-8">Submit your business details. Once approved, you'll see distributor pricing and can place wholesale orders.</p>

            <form onSubmit={submit} className="bg-white border border-stone-200 p-8 space-y-5">
                <h2 className="font-serif text-2xl">Account</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Login email *" v={f.email} on={set("email")} type="email" t="email" />
                    <Field label="Password *" v={f.password} on={set("password")} type="password" t="password" />
                </div>
                <h2 className="font-serif text-2xl pt-3 border-t border-stone-100">Business</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Business name *" v={f.business_name} on={set("business_name")} t="business" />
                    <Field label="Contact person *" v={f.contact_name} on={set("contact_name")} t="contact" />
                    <Field label="Phone *" v={f.phone} on={set("phone")} t="phone" />
                    <Field label="GSTIN" v={f.gst_number} on={set("gst_number")} t="gstin" />
                    <Field label="Drug license #" v={f.license_number} on={set("license_number")} t="license" />
                </div>
                <h2 className="font-serif text-2xl pt-3 border-t border-stone-100">Address</h2>
                <Field label="Address line 1 *" v={f.address_line1} on={set("address_line1")} t="line1" />
                <Field label="Address line 2" v={f.address_line2} on={set("address_line2")} t="line2" />
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="City *" v={f.city} on={set("city")} t="city" />
                    <Field label="State *" v={f.state} on={set("state")} t="state" />
                    <Field label="Pincode *" v={f.pincode} on={set("pincode")} t="pincode" />
                </div>
                <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full py-6" data-testid="distributor-submit">
                    {busy ? "Submitting…" : "Submit application"}
                </Button>
                <p className="text-xs text-center text-stone-500">Already have an account? <Link to="/login?portal=distributor" className="text-sky-700 underline">Sign in</Link></p>
            </form>
        </div>
    );
}

const Field = ({ label, v, on, type = "text", t }) => (
    <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input type={type} value={v} onChange={on} className="rounded-none border-stone-300 mt-1" data-testid={`dist-${t}`} />
    </div>
);
